import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Alert, Button } from "@agentnative/ui";
import { AuthLayout } from "../components/AuthLayout";
import { verifyEmail } from "../lib/api/auth";
import { apiErrorMessage } from "../lib/api/client";
import { useAuthStore } from "../stores/auth";

type State = { kind: "verifying" } | { kind: "ok" } | { kind: "failed"; message: string };

// Landing page for the link in the verification email. Redeems the token
// once on mount (the ref guards React StrictMode's double effect, which
// would otherwise spend the single-use token and then show a failure).
export function VerifyEmailRoute() {
  const [state, setState] = useState<State>({ kind: "verifying" });
  const ran = useRef(false);
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = new URLSearchParams(window.location.search).get("token") ?? "";
    if (!token) {
      setState({ kind: "failed", message: "This verification link is missing its token." });
      return;
    }
    verifyEmail(token)
      .then(() => {
        setState({ kind: "ok" });
        // Keep the banner honest if this browser is signed in as that user.
        if (user) setUser({ ...user, emailVerified: true });
      })
      .catch((err) => setState({ kind: "failed", message: apiErrorMessage(err) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthLayout title="Verify your email" subtitle="Confirming your email address">
      <div className="flex flex-col gap-4">
        {state.kind === "verifying" && <p className="font-sans text-sm">Verifying...</p>}
        {state.kind === "ok" && (
          <>
            <Alert variant="success" title="Email verified">
              Your email address is confirmed. You can now sign in to apps with Agent Native.
            </Alert>
            <Link to="/">
              <Button variant="primary" className="min-h-11">Continue</Button>
            </Link>
          </>
        )}
        {state.kind === "failed" && (
          <>
            <Alert variant="danger" title="Could not verify">
              {state.message} The link may have expired or already been used. Sign in and request a new one.
            </Alert>
            <Link to="/login">
              <Button variant="primary" className="min-h-11">Go to sign in</Button>
            </Link>
          </>
        )}
      </div>
    </AuthLayout>
  );
}
